/**
 * @file     rentRange.ts
 * @readme   ./CLAUDE.md
 * @purpose  The 'lease' kind value read. Two bands (p25, median, p75) for the
 *           same bedroom count and a comparable home type, widening 1, 2 then
 *           3 km until 8 are found: an ASKING band from active IDX rentals,
 *           which anyone may see, and a LEASED band plus the comparable list
 *           from closed leases, which are VOW data and are built only for a
 *           caller who passes the VOW registrant gate (VOW-derived numbers
 *           never render publicly). Deterministic; no AI on MLS rows (ADR-009).
 *           Never returns dates.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { getListingKind, type LEASE_HOME_TYPES } from '@/core/config/listingKinds';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Db = SupabaseClient<any>;

export type LeaseHomeType = (typeof LEASE_HOME_TYPES)[number];

/**
 * Owner home type to the feed's PropertySubType values (checked live
 * 2026-10-10). Suites, laneways and "other" have no feed equivalent, so they
 * compare on bedrooms alone and say so in `method`.
 */
export const LEASE_HOME_TYPE_SUB_TYPES: Record<LeaseHomeType, readonly string[] | null> = {
  apartment_condo: ['Apartment/Condo'],
  townhouse: ['Townhouse', 'Row House (Non-Strata)'],
  house: ['Single Family Residence'],
  duplex: ['Half Duplex'],
  suite: null,
  laneway: null,
  other: null,
};

/** A feed PropertySubType back to the owner home type it compares as; anything unmapped compares on bedrooms alone. */
export function leaseHomeTypeForSubType(subType: string | null | undefined): LeaseHomeType {
  if (subType) {
    for (const [homeType, subTypes] of Object.entries(LEASE_HOME_TYPE_SUB_TYPES)) {
      if (subTypes?.includes(subType)) return homeType as LeaseHomeType;
    }
  }
  return 'other';
}

export const RENT_RANGE_RADII_M = [1000, 2000, 3000] as const;
export const RENT_RANGE_MIN_COMPARABLES = 8;
export const RENT_RANGE_MAX_LISTED = 25;

export type RentRangeMethod = 'leased_comparables_same_type' | 'leased_comparables_any_type' | 'asking_rents_same_type' | 'asking_rents_any_type';

export type RentRangeInput = { latitude: number; longitude: number; bedrooms: number; homeType: LeaseHomeType };

export type RentBand = { low: number; high: number; typical: number; count: number; radiusKm: number; method: RentRangeMethod };

export type RentRangeResult = RentBand | { insufficient: true };

export type RentComparable = {
  address: string;
  bedrooms: number | null;
  bathrooms: number | null;
  livingArea: number | null;
  /** Null when only the IDX record exists: the feed carries no close price there. */
  leasedRent: number | null;
  listRent: number | null;
  officeName: string | null;
};

export type BandRow = { radius_m: number; comparables: number; p25: number | null; p50: number | null; p75: number | null };

export function methodFor(homeType: LeaseHomeType, source: 'leased' | 'asking' = 'leased'): RentRangeMethod {
  const same = Boolean(LEASE_HOME_TYPE_SUB_TYPES[homeType]);
  if (source === 'asking') return same ? 'asking_rents_same_type' : 'asking_rents_any_type';
  return same ? 'leased_comparables_same_type' : 'leased_comparables_any_type';
}

/** The smallest radius with enough comparables, or insufficient below the floor at the widest one. */
export function pickBand(rows: BandRow[], method: RentRangeMethod, min = RENT_RANGE_MIN_COMPARABLES): RentRangeResult {
  const sorted = [...rows].sort((a, b) => a.radius_m - b.radius_m);
  const hit = sorted.find((r) => Number(r.comparables) >= min && r.p25 != null && r.p50 != null && r.p75 != null);
  if (!hit) return { insufficient: true };
  return {
    low: Number(hit.p25),
    high: Number(hit.p75),
    typical: Number(hit.p50),
    count: Number(hit.comparables),
    radiusKm: hit.radius_m / 1000,
    method,
  };
}

export function isRentBand(r: RentRangeResult): r is RentBand {
  return !('insufficient' in r);
}

/** Where a rent sits against a band: under the 25th percentile, inside the middle half, or over the 75th. */
export function bandPosition(rent: number | null, band: RentRangeResult): 'below' | 'within' | 'above' | null {
  if (rent == null || !isRentBand(band)) return null;
  if (rent < band.low) return 'below';
  if (rent > band.high) return 'above';
  return 'within';
}

function rpcArgs(input: RentRangeInput) {
  const { min, max } = getListingKind('lease').priceRange;
  return {
    p_latitude: input.latitude,
    p_longitude: input.longitude,
    p_bedrooms: input.bedrooms,
    p_sub_types: LEASE_HOME_TYPE_SUB_TYPES[input.homeType],
    p_min_rent: min,
    p_max_rent: max,
  };
}

const num = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

export class RentRangeService {
  constructor(private readonly db: Db) {}

  /** Public: active IDX asking rents nearby. */
  async askingBand(input: RentRangeInput, excludeListingKey?: string): Promise<RentRangeResult> {
    const { data, error } = await this.db.rpc('lease_asking_band', {
      p_latitude: input.latitude,
      p_longitude: input.longitude,
      p_bedrooms: input.bedrooms,
      p_sub_types: LEASE_HOME_TYPE_SUB_TYPES[input.homeType],
      p_radii: [...RENT_RANGE_RADII_M],
      p_exclude_listing_key: excludeListingKey ?? null,
    });
    if (error) throw new Error(`lease_asking_band: ${error.message}`);
    const rows = ((data ?? []) as Array<{ radius_m: number; listings: number; p25: number | null; p50: number | null; p75: number | null }>)
      .map((r) => ({ radius_m: r.radius_m, comparables: r.listings, p25: r.p25, p50: r.p50, p75: r.p75 }));
    return pickBand(rows, methodFor(input.homeType, 'asking'));
  }

  /** VOW data: call only for a caller who passed evaluateVowGate, and audit the read. */
  async band(input: RentRangeInput): Promise<RentRangeResult> {
    const { data, error } = await this.db.rpc('lease_rent_band', { ...rpcArgs(input), p_radii: [...RENT_RANGE_RADII_M] });
    if (error) throw new Error(`lease_rent_band: ${error.message}`);
    return pickBand((data ?? []) as BandRow[], methodFor(input.homeType));
  }

  /** VOW data: call only for a caller who passed evaluateVowGate, and audit the read. */
  async comparables(input: RentRangeInput, radiusKm: number, limit = RENT_RANGE_MAX_LISTED): Promise<{ comparables: RentComparable[]; listingKeys: string[] }> {
    const { data, error } = await this.db.rpc('lease_rent_comparables', {
      ...rpcArgs(input),
      p_radius_m: Math.round(radiusKm * 1000),
      p_limit: limit,
    });
    if (error) throw new Error(`lease_rent_comparables: ${error.message}`);
    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return {
      listingKeys: rows.map((r) => String(r.listing_key)),
      comparables: rows.map((r) => ({
        address: String(r.address ?? ''),
        bedrooms: num(r.bedrooms),
        bathrooms: num(r.bathrooms),
        livingArea: num(r.living_area),
        leasedRent: r.rent_source === 'leased' ? num(r.rent) : null,
        listRent: num(r.list_price),
        officeName: (r.office_name as string | null) ?? null,
      })),
    };
  }

  /**
   * The value read a route returns: the asking band for anyone, and the leased
   * band with its comparable list only when `hasVowAccess` is true.
   * `excludeListingKey` keeps a rental out of its own asking band.
   */
  async estimate(
    input: RentRangeInput,
    opts: { hasVowAccess: boolean; excludeListingKey?: string },
  ): Promise<{ asking: RentRangeResult; leased: RentRangeResult | null; comparables: RentComparable[] | null; listingKeys: string[] }> {
    const asking = await this.askingBand(input, opts.excludeListingKey);
    if (!opts.hasVowAccess) return { asking, leased: null, comparables: null, listingKeys: [] };
    const leased = await this.band(input);
    if (!isRentBand(leased)) return { asking, leased, comparables: null, listingKeys: [] };
    const { comparables, listingKeys } = await this.comparables(input, leased.radiusKm);
    return { asking, leased, comparables, listingKeys };
  }
}
